import { createContext, useContext, useState, useCallback } from 'react';
import { Project } from '../@types/projects/types';
import { Well } from '../@types/wells/types';
import { Sample } from '../@types/samples/types';

interface SelectContextProps {
  selectedInfo: Info;
  setSelectedInfo: (newInfo: Partial<Info> | ((prev: Info) => Partial<Info>)) => void;
  isSelectedProjectOwner: (userUid?: string) => boolean;
}

export interface Info {
  sample?: Sample;
  project?: Project;
  well?: Well;
}

const SelectContext = createContext<SelectContextProps | undefined>(undefined);

export const SelectProvider = ({
  children
}: React.PropsWithChildren) => {
  const [selectedInfo, setInfo] = useState<Info>({});

  const setSelectedInfo = useCallback((newInfo: Partial<Info> | ((prev: Info) => Partial<Info>)) => {
    if (typeof newInfo === 'function') {
      setInfo(oldInfo => ({ ...oldInfo, ...newInfo(oldInfo) }));
    } else {
      setInfo(oldInfo => ({ ...oldInfo, ...newInfo }));
    }
  }, []);

  const isSelectedProjectOwner = useCallback((userUid?: string) => {
    if (!userUid || !selectedInfo.project?.users?.length) {
      return false;
    }

    return selectedInfo.project.users.some(
      user => user.uid === userUid && user.role === 'owner'
    );
  }, [selectedInfo.project?.users]);

  return (
    <SelectContext.Provider value={{ selectedInfo, setSelectedInfo, isSelectedProjectOwner }}>
      {children}
    </SelectContext.Provider>
  );
};

export const useSelected = (): SelectContextProps => {
  const context = useContext(SelectContext);
  if (!context) {
    throw new Error('useSelected must be used within a SelectProvider');
  }
  return context;
};
